import React, { useState } from "react";
import {
    Button,
    Divider,
    Drawer,
    FormControlLabel,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Switch,
    Tooltip,
    Typography
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo, faHouse } from "@fortawesome/sharp-duotone-light-svg-icons";
import {
    ADMIN_CONTROLS,
    AUTO_REFRESH_TEXT,
    AUTO_REFRESH_TOOLTIP,
    MAP_CONTROLS,
    MY_PRODUCTS,
    USER_PROFILE_HEADER,
    USER_TIPS
} from "../UserVisibleStrings";
import useIdentity from "../../app/auth";
import OpacitySlider from "./OpacitySlider";
import MapStateToggle from "./MapStyleSelector";
import { closeDrawer, useIsRightBarExpanded } from "../../features/Drawer/drawerSlice";
import OpacityToggle from "./OpacityToggle";
import {
    setShouldAutoRefreshWindow,
    useForecastTime,
    useShouldAutoRefreshWindow
} from "../../features/surface/surfaceSlice";
import { formatForecastTime } from "../../features/surface/surfaceUtils";
import { useDispatch } from "react-redux";
import { useIsMobile } from "../../features/pageWidth/pageWidthSlice";
import {
    RiskViewerKind,
    RouteViewState,
    useRiskViewerStateKind,
    useRouteViewState
} from "../../features/riskViewer/riskViewerSlice";
import { setUserViewOverride, useUserViewOverride } from "../../features/riskViewer/userViewOverrideSlice";
import Cookies from "js-cookie";
import { useShouldShowAlerts } from "../../features/alertDisplay/alertDisplaySlice";
import { MongoStatisticsModal } from "./MongoStatisticsModal";
import { HistoricalForecastsModal } from "./HistoricalForecastsModal";
import {
    toggleIsShowingWindStreamlinesTuningPanel,
    useSelectIsShowingWindStreamlinesTuningPanel
} from "../../features/mapDisplay/mapDisplaySlice";

/**
 * UserProfileDrawer component.
 *
 * @returns {JSX.Element} The rendered component.
 */
const UserProfileDrawer: React.FC = ( ) =>
{
    const userViewOverride = useUserViewOverride();
    const [ showDashboardStatistics, setShowDashboardStatistics ] = useState<boolean>( false );
    const [ showHistorical, setShowHistorical ] = useState<boolean>( false );
    const { identity } = useIdentity( userViewOverride );
    const nonOverrideableIsAdminCheck = ( identity && String( identity["custom:id"] ).replaceAll( / /g, "" ) === "0" ) ?? false;
    const isExpanded = useIsRightBarExpanded();
    const isMobile = useIsMobile();
    const dispatch = useDispatch();
    const handleDrawerClose = () => dispatch( closeDrawer() );
    const forecastTimeData = useForecastTime();
    const formattedForecastTime = formatForecastTime( forecastTimeData?.date, forecastTimeData?.hour, true );
    const riskViewerStateKind = useRiskViewerStateKind();
    const shouldAutoRefreshWindow = useShouldAutoRefreshWindow();
    const routeViewState = useRouteViewState();
    const isShowingAlerts = useShouldShowAlerts();
    const isShowingWindStreamlinesTuningPanel = useSelectIsShowingWindStreamlinesTuningPanel();

    const handleToggleOverride = () =>
    {
        const newState = !userViewOverride;
        dispatch( setUserViewOverride( newState ) );
    };

    return (
        <Drawer
            anchor = "right"
            variant = "persistent"
            open = {isExpanded}
            onClose = {handleDrawerClose}
            sx = {{
                paddingRight: 2,
                paddingLeft: 2,
            }}
            PaperProps = {{
                sx: { width: isMobile ? "100%" : "330px" },
            }}
        >
            <div style = {{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px" }}>
                <Typography variant = "h6"
                    sx = {{ paddingLeft: 2 }}
                >{identity ? `Hello, ${String( identity["email"] )}` : USER_PROFILE_HEADER}
                </Typography>
                <IconButton onClick = {handleDrawerClose}>
                    <CloseIcon/>
                </IconButton>
            </div>
            <div>
                <List sx = {{ marginTop: 0 }}>
                    <ListItem disablePadding>
                        <Tooltip title = {MY_PRODUCTS} placement = "left">
                            <ListItemButton component = "a" href = "https://rvweather.com/members-only/">
                                <ListItemIcon>
                                    <FontAwesomeIcon icon = {faHouse} size = "2x" fixedWidth/>
                                </ListItemIcon>
                                <ListItemText primary = {MY_PRODUCTS}/>
                            </ListItemButton>
                        </Tooltip>
                    </ListItem>
                    <ListItem disablePadding>
                        <Tooltip title = {USER_TIPS} placement = "left">
                            <ListItemButton
                                component = "a"
                                href = "https://rvweather.com/user-tips/?"
                                target = "_blank"
                            >
                                <ListItemIcon>
                                    <FontAwesomeIcon icon = {faCircleInfo} size = "2x" fixedWidth/>
                                </ListItemIcon>
                                <ListItemText primary = {USER_TIPS}/>
                            </ListItemButton>
                        </Tooltip>
                    </ListItem>
                </List>
            </div>
            <Divider sx = {{ marginTop: 1, marginBottom: 1 }}/>
            <div>
                <Typography variant = "h6" sx = {{ paddingLeft: 2 }}>{MAP_CONTROLS}</Typography>
                {routeViewState !== RouteViewState.COLORED_ROUTE && <OpacitySlider/>}
                {!isShowingAlerts && routeViewState !== RouteViewState.COLORED_ROUTE && <OpacityToggle/>}
                <MapStateToggle/>
                <Typography variant = "body1"
                    sx = {{ paddingLeft: 2 }}
                >{`Forecast Time: ${formattedForecastTime}`}
                </Typography>
                {riskViewerStateKind !== RiskViewerKind.TRIP_STATE &&
                <Tooltip title = {AUTO_REFRESH_TOOLTIP}>
                    <FormControlLabel
                        control = {<Switch checked = {shouldAutoRefreshWindow} onChange = {() => dispatch( setShouldAutoRefreshWindow( !shouldAutoRefreshWindow ) )}/>}
                        label = {AUTO_REFRESH_TEXT}
                        sx = {{ paddingLeft: "16px" }}
                    />
                </Tooltip>}
            </div>

            {nonOverrideableIsAdminCheck && (
                <React.Fragment>
                    <Divider sx = {{ marginTop: 1, marginBottom: 1 }}/>
                    <Typography variant = "h6" sx = {{ paddingLeft: 2 }}>{ADMIN_CONTROLS}</Typography>
                    <div style = {{ padding: "0 16px 16px 16px" }}>
                        <Button
                            variant = "contained"
                            color = {userViewOverride ? "secondary" : "primary"}
                            onClick = {handleToggleOverride}
                            fullWidth
                        >
                            {userViewOverride ? "Disable User View Override" : "Enable User View Override"}
                        </Button>
                    </div>
                    <div style = {{ padding: "0 16px 16px 16px" }}>
                        <Button
                            variant = "contained"
                            color = {Cookies.get( "hasSeenTutorial" ) === undefined ? "secondary" : "primary"}
                            onClick = {() => Cookies.remove( "hasSeenTutorial" )}
                            fullWidth
                        >
                            Reset Tutorial
                        </Button>
                    </div>
                    <div style = {{ padding: "0 16px 16px 16px" }}>
                        <Button
                            variant = "contained"
                            color = {isShowingWindStreamlinesTuningPanel ? "secondary" : "primary"}
                            onClick = {() => dispatch( toggleIsShowingWindStreamlinesTuningPanel() )}
                            fullWidth
                        >
                            {isShowingWindStreamlinesTuningPanel ? "Disable Wind Streamline parameter editor" : "Enable Wind streamline parameter editor"}
                        </Button>
                    </div>

                    <div style = {{ padding: "0 16px 16px 16px" }}>
                        <Button
                            variant = "contained"
                            color = "primary"
                            onClick = {() => setShowDashboardStatistics( true )}
                            fullWidth
                        >
                            View Statistics
                        </Button>
                    </div>

                    <div style = {{ padding: "0 16px 16px 16px" }}>
                        <Button
                            variant = "contained"
                            color = "primary"
                            onClick = {() => setShowHistorical( true )}
                            fullWidth
                        >
                            Control Historical Forecasts
                        </Button>
                    </div>
                    <MongoStatisticsModal open = {showDashboardStatistics}
                        onClose = {() => setShowDashboardStatistics( false )}
                    />
                    {showHistorical && <HistoricalForecastsModal open = {showHistorical} onClose = {() => setShowHistorical( false )}/>}
                </React.Fragment>
            )}
        </Drawer>
    );
};

export default UserProfileDrawer;
